import {Avatar, Dropdown, Image, Menu} from 'antd';
import { FC, ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import i18next from 'i18next';

import {LOGOUT_LINK, MY_PROFILE_PAGE_LINK, resources} from 'commons/constants';
import useLanguage from 'hooks/useLanguage';
import { authorize } from 'services/auth';
import { getRedirectUrl, removeAccessToken } from 'utils/storage';
import { storeName } from 'store/models/layout.model';
import { storeName as userModelStoreName } from 'store/models/user.model';

import './styles.scss';

type LayoutMainPropsTypes = {
  children: ReactNode;
};

const LayoutMain: FC<LayoutMainPropsTypes> = ({ children }) => {
  const history = useHistory();
  const { setLanguage } = useLanguage();

  const { navbarExpand } = useSelector((state) => state[storeName].navbarConfig);
  const user = useSelector((state) => state[userModelStoreName].user);
  const dispatch = useDispatch();

  const languageMenu = (
    <Menu>
      {Object.keys(resources).map((key) => {
        const lang = resources[key];

        return (
          <Menu.Item key={lang.key} icon={lang.icon} onClick={() => setLanguage(lang.key)}>
            {lang.name}
          </Menu.Item>
        );
      })}
    </Menu>
  );

  const getUser = () => {
    dispatch[userModelStoreName]
      .getUserDetailAsync()
      .then((userResult: any) => {
        if (!userResult) {
          authorize();
        } else {
          const redirectUrl = getRedirectUrl();
          if (redirectUrl) {
            const url = new URL(redirectUrl);
            history.push(`${url.pathname}${url.search}`);
          }
        }
      })
      .catch((err: any) => {
        console.error('useAuth:', err);
        authorize();
      });
  };

  const userMenu = (
    <Menu>
      {user ? (
        <>
          <Menu.Item
            key="myAccount"
            icon={<i className="fa fa-solid fa-user fa-sm fa-fw mr-2 text-gray-500" />}
            onClick={() => history.push(MY_PROFILE_PAGE_LINK)}
          >
            Manage my account
          </Menu.Item>

          <Menu.Item
            key="authorized"
            icon={<i className="fas fa-sign-out-alt fa-sm fa-fw mr-2 text-gray-500" />}
            onClick={() => {
              removeAccessToken();
              window.location.href = LOGOUT_LINK;
            }}
          >
            Logout
          </Menu.Item>
        </>
      ) : (
        <Menu.Item
          icon={<i className="fas fa-sign-out-alt fa-sm fa-fw mr-2 text-gray-500" />}
          onClick={() => {
            getUser();
          }}
        >
          Login
        </Menu.Item>
      )}
    </Menu>
  );

  return (
    <div className="clik-account__main">
      <header className="main__header">
        <div className="header__navbar-action">
          <i
            className="icon-menu_hamburger_24x24 header__action"
            onClick={() => dispatch[storeName].toggleNavbarConfig(!navbarExpand)}
          />
        </div>
        <div className="header__actions">
          <Dropdown overlay={languageMenu} placement="bottom">
            <div className="action__language">
              {resources[i18next.language] ? (
                <>
                  {resources[i18next.language].icon}
                  <div className="language__short-name">
                    {resources[i18next.language].shortDisplayName}
                  </div>
                </>
              ) : null}
            </div>
          </Dropdown>
          <Dropdown overlay={userMenu} placement="topRight">
            <div className="action__user">
              <div className="user__info">
                <span className="info__name">{user?.name}</span>
                <span className="info__role">{user?.email}</span>
              </div>
              <div className="user__avatar">
                <Avatar src={<Image preview={false} src={user?.avatar} />} />
              </div>
            </div>
          </Dropdown>
        </div>
      </header>
      <div className="main__content">{children}</div>
      <div className="main__footer">
        <span>Powered by</span>
        <i className="icon-logo_clik_44x18" />
      </div>
    </div>
  );
};

export default LayoutMain;
